import {Controller, useFormContext} from 'react-hook-form'
import {MedicalReportFieldsSchema} from '../../formSchemas'
import {z} from 'zod'
import {useTranslation} from '@/translations/i18n'
import {Switch} from '@/components/commons/switch/Switch'
import {SwitchField} from '../switch-field/SwitchField'
import {InputText} from '@/components/commons/input-text/InputText'

const SchoolFieldSchema = MedicalReportFieldsSchema.pick({
    hasSupportTeacher: true,
    supportTeacherHours: true,
    hasOepa: true,
    oepaHours: true
})
type SchoolFieldSchema = z.infer<typeof SchoolFieldSchema>

interface SchoolSwitchFieldProps {
    name: keyof Pick<SchoolFieldSchema, 'hasSupportTeacher' | 'hasOepa'>
    label: string
    relatedFieldName: keyof Pick<SchoolFieldSchema, 'supportTeacherHours' | 'oepaHours'>
    relatedFieldLabel: string
    onToggle?: (value: boolean) => void
}

export const SchoolSwitchField = ({
    name,
    label,
    relatedFieldName,
    relatedFieldLabel,
    onToggle
}: SchoolSwitchFieldProps) => {
    const {t} = useTranslation()
    const {register, control, setValue} = useFormContext<SchoolFieldSchema>()

    return (
        <Controller
            control={control}
            name={name}
            render={({field}) => (
                <SwitchField columns={2} height="58px" columnGap={4} fullWidth>
                    <Switch
                        size="sm"
                        label={label}
                        defaultChecked={field.value}
                        onChange={value => {
                            field.onChange(value)
                            if (!value && relatedFieldName) {
                                setValue(relatedFieldName, 0)
                            }
                            onToggle?.(value)
                        }}
                    />
                    {field.value && relatedFieldName && (
                        <InputText
                            type="number"
                            label={relatedFieldLabel}
                            placeholder={t('medical_records:insert_info')}
                            inputMode="decimal"
                            step="1"
                            {...register(relatedFieldName, {
                                setValueAs: v => (v === '' ? 0 : parseInt(v, 10))
                            })}
                        />
                    )}
                </SwitchField>
            )}
        />
    )
}
